import { useError } from '../useError'
import qz from 'qz-tray'
import { useCallback, useState } from 'react'
import type { QzConsumerProps, QzOption } from './useQz'
import map from 'lodash/map'
import isEmpty from 'lodash/isEmpty'
import { useDeepCompareEffect } from '../useDeepCompareEffect'

export type QzPrinter = {
  label: string
  value: string
  qzOption: QzOption
}
export const useQzPrinterList = (
  props: QzConsumerProps
): {
  printers: QzPrinter[]
  loading: boolean
  getPrinters: () => Promise<QzPrinter[]>
} => {
  const { onError } = useError()
  const { initialize, qzOptions } = props.useQz()
  const [printers, setPrinters] = useState<QzPrinter[]>([])
  const [loading, setLoading] = useState(false)

  const getPrinters = useCallback(async () => {
    try {
      setLoading(true)
      if (isEmpty(qzOptions)) return

      const _printers: QzPrinter[] = []

      //we need to be on for of to run the on concurrent
      //we cannot do in parallel because of qzTray instance creation
      for (const qzOption of qzOptions) {
        try {
          await initialize(qzOption)

          const printers = await qz.printers.find()

          const currentPrinters = map(printers, (printer: string) => {
            return {
              label: `${printer} from '${qzOption.host}'`,
              value: printer,
              //we just include the host, the certificate and signature will be retrieved from the qzOptions
              qzOption: { host: qzOption.host },
            }
          })
          _printers.push(...currentPrinters)
        } catch (err) {
          onError(err)
        } finally {
          if (qz.websocket.isActive()) {
            await qz.websocket.disconnect()
          }
        }
      }

      return _printers
    } finally {
      setLoading(false)
    }
  }, [initialize, qzOptions, onError])

  useDeepCompareEffect(() => {
    getPrinters().then((_printers) => {
      setPrinters(_printers)
    })
  }, [initialize, qzOptions])

  return { printers, loading, getPrinters }
}
